import Phaser from 'phaser';
import { chapterName, chapterTagline, CHAPTERS } from '../levels/manifest';
import { Progress, computeUnlocks } from '../progress';
import { Cloud } from '../cloud';
import { Backdrop } from '../render/Backdrop';
import { Sfx } from '../audio/sfx';
import { drawWatermark } from '../render/watermark';
import { dailyKeyFor, dailyKeyYesterday, dailyIdFor, dailyDateLabel } from '../levels/procgen';
import { achievementList, unlockedCount, ACHIEVEMENTS } from '../achievements';
import { fmtScore } from '../score';
import { AD_COPY } from '../ad-copy';
import { APP_VERSION } from '../version';
import { DEV_MENU_ENABLED, openDevMenu } from '../devmenu';
import { SANS, TYPE, UI, Panel, openModal, uiButton, clean, padlock, dotRow } from '../render/theme';
import { t, tf, FONT_STACK, LANGS, getLang, setLang, type LangCode } from '../i18n';

/**
 * Menu = chapter list. Tap a chapter to drop into its level select
 * (ChapterScene). Chapters are gated: chapter N+1 unlocks when chapter N's
 * real slots are all solved.
 */
export class MenuScene extends Phaser.Scene {
  private sfx!: Sfx;
  /** Accumulated pointer travel since pointerdown — distinguishes tap vs scroll. */
  private _dragDist = 0;
  private _maxScroll = 0;

  constructor() {
    super('Menu');
  }

  create() {
    this.sfx = (this.registry.get('sfx') as Sfx | undefined) ?? new Sfx();
    this.registry.set('sfx', this.sfx);
    this.sfx.init();

    new Backdrop(this, { glow: 0.3, scanlines: false, particles: false });
    const { width, height } = this.scale;
    const M = 72;                 // side margin
    const contentW = width - M * 2;

    // === HEADER === wordmark, one quiet status line, one thin progress rule
    this.add.text(M, 70, 'NØDUS', {
      fontFamily: SANS, fontSize: `${TYPE.hero}px`, color: UI.text, fontStyle: '800',
    });

    const allRealLevelIds = CHAPTERS.flatMap(c => c.slots.filter((s): s is string => !!s));
    const totalSolved = allRealLevelIds.filter(id => Progress.isSolved(id)).length;
    const totalReal = allRealLevelIds.length;
    const pct = totalReal > 0 ? totalSolved / totalReal : 0;

    this.add.text(M, 196, `${totalSolved} / ${totalReal}  ${t('menu_signals_down')}`, {
      fontFamily: SANS, fontSize: `${TYPE.body - 2}px`, color: UI.textDim,
    });

    const barY = 258;
    const bar = this.add.graphics();
    bar.fillStyle(UI.hairline, 1);
    bar.fillRoundedRect(M, barY - 4, contentW, 8, 4);
    if (pct > 0) {
      bar.fillStyle(UI.signal, 1);
      bar.fillRoundedRect(M, barY - 4, Math.max(8, contentW * pct), 8, 4);
    }

    // === CARDS === daily + endless are the two heroes, chapters follow
    const unlocks = computeUnlocks(CHAPTERS);
    const chapterTop = 310;
    const heroH = 268;
    const cardH = 196;
    const cardGap = 22;
    const cardX = M;
    const cardW = contentW;

    this.makeDailyCard(cardX, chapterTop, cardW, heroH);
    this.makeEndlessCard(cardX, chapterTop + heroH + cardGap, cardW, heroH);

    const chaptersTop = chapterTop + (heroH + cardGap) * 2 + 30;
    this.add.text(M, chaptersTop, t('sectors'), {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: UI.textFaint, fontStyle: '700',
    });
    const listTop = chaptersTop + 52;

    CHAPTERS.forEach((ch, i) => {
      const y = listTop + i * (cardH + cardGap);
      const realIds = ch.slots.filter((s): s is string => !!s);
      const isChapterUnlocked = realIds.length === 0 ? false : !!unlocks[realIds[0]];
      const chapterSolved = realIds.length > 0 && realIds.every(id => Progress.isSolved(id));
      this.makeChapterCard(cardX, y, cardW, cardH, ch, isChapterUnlocked, chapterSolved);
    });

    // === SCROLLING === drag or wheel; cards fire on pointer-up only when the
    // pointer barely moved (a tap, not a scroll).
    const footerH = 150;
    const contentBottom = listTop + CHAPTERS.length * (cardH + cardGap) + footerH + 40;
    this._maxScroll = Math.max(0, contentBottom - height);
    if (this._maxScroll > 0) {
      this.cameras.main.setBounds(0, 0, width, contentBottom);
      let dragging = false;
      let lastPy = 0;
      this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
        dragging = true; lastPy = p.y; this._dragDist = 0;
      });
      this.input.on('pointerup', () => { dragging = false; });
      this.input.on('pointerupoutside', () => { dragging = false; });
      this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
        if (!dragging) return;
        const dy = p.y - lastPy; lastPy = p.y;
        this._dragDist += Math.abs(dy);
        this.cameras.main.scrollY = Phaser.Math.Clamp(
          this.cameras.main.scrollY - dy, 0, this._maxScroll,
        );
      });
      this.input.on('wheel', (_p: unknown, _go: unknown, _dx: number, dy: number) => {
        this.cameras.main.scrollY = Phaser.Math.Clamp(
          this.cameras.main.scrollY + dy * 0.6, 0, this._maxScroll,
        );
      });
    }

    // === FOOTER === a pinned tab bar of three real buttons (each well over
    // 100 design px tall). The credit line sits below it, never under it.
    const barTop = height - footerH + 8;
    const barH = 100;
    const footBg = this.add.graphics().setScrollFactor(0).setDepth(900);
    footBg.fillStyle(0x070b18, 1);
    footBg.fillRect(0, height - footerH, width, footerH);
    footBg.lineStyle(1, UI.hairline, 1);
    footBg.lineBetween(0, height - footerH, width, height - footerH);

    const tab = (cx: number, label: string, color: string, hover: string, open: () => void) => {
      const txt = this.add.text(cx, barTop + barH / 2, label, {
        fontFamily: SANS, fontSize: `${TYPE.body - 2}px`, color, fontStyle: '700',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(901);
      const hit = this.add.rectangle(cx, barTop + barH / 2, width / 3, 132, 0xffffff, 0)
        .setScrollFactor(0).setDepth(902).setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => txt.setColor(hover));
      hit.on('pointerout', () => txt.setColor(color));
      hit.on('pointerup', () => {
        if (this._dragDist > 12) return;
        this.sfx.ui();
        open();
      });
    };
    tab(width / 6, t('menu_settings'), UI.accentHex, '#ffffff', () => this.showSettings());
    tab(width / 2, t('menu_achievements'), UI.warm, '#ffe9b0', () => this.showAchievements());
    tab((width * 5) / 6, t('menu_stats'), UI.accentHex, '#ffffff', () => this.showStats());

    // === LANGUAGE PICKER ===
    this.buildLanguagePicker(width, height);

    drawWatermark(this);
  }

  /** Lifetime statistics overlay. */
  private showStats() {
    const s = Progress.stats();
    const best = Progress.endlessBest();
    const rows: [string, string][] = [
      [t('stats_levels_completed'), String(s.wins)],
      [t('stats_endless_best'), best >= 0 ? `LV ${best + 1}` : '-'],
      [t('stats_best_streak'), `×${Progress.endlessBestStreak()}`],
      [t('stats_fail_rate'), `${Math.round(Progress.failRate() * 100)}%`],
      [t('stats_wins'), String(s.wins)],
      [t('stats_fails'), String(s.fails)],
      [t('stats_hints_used'), String(s.hintsUsed)],
      [t('stats_ads_watched'), String(s.adsWatched)],
    ];
    const rowH = 84;
    const m = openModal(this, {
      title: t('stats_title'), accent: UI.accent, accentHex: UI.accentHex,
      h: 140 + rows.length * rowH + 170,
    });
    rows.forEach(([label, val], i) => {
      const ry = m.top + i * rowH + rowH / 2 - 10;
      m.root.add(this.add.text(m.left, ry, label, {
        fontFamily: SANS, fontSize: '30px', color: UI.textDim, wordWrap: { width: m.w - 260 },
      }).setOrigin(0, 0.5));
      m.root.add(this.add.text(m.right, ry, val, {
        fontFamily: SANS, fontSize: '34px', color: UI.text, fontStyle: '800',
      }).setOrigin(1, 0.5));
      if (i < rows.length - 1) {
        m.root.add(this.add.rectangle(m.cx, ry + rowH / 2, m.w - 96, 1, UI.hairline, 1));
      }
    });
    const dismiss = () => { this.sfx.ui(); m.root.destroy(); };
    m.root.add(uiButton(this, m.cx, m.cy + m.h / 2 - 86, m.w - 96, 108, t('stats_close'), UI.accent, dismiss));
    m.dim.on('pointerup', dismiss);
  }

  /** Settings overlay: sound, haptics, and the reset controls. */
  private showSettings(resetArmed = false) {
    const cloud = Cloud.configured();
    const user = cloud ? Cloud.currentUser() : null;
    const toggleH = 124, actionH = 100;
    const nActions = 2 + (cloud ? (user ? 2 : 1) : 0);
    const m = openModal(this, {
      title: t('settings_title'), accent: UI.accent, accentHex: UI.accentHex,
      h: 140 + toggleH * 2 + actionH * nActions + 190 + 330,
    });
    const reopen = (armed = false) => { m.root.destroy(); this.showSettings(armed); };
    let ry = m.top;

    // Toggle row with a real switch graphic, whole row is the hit area.
    const toggleRow = (label: string, on: boolean, onTap: () => void, note?: string) => {
      m.root.add(this.add.text(m.left, ry + toggleH / 2 - (note ? 16 : 0), label, {
        fontFamily: SANS, fontSize: '32px', color: UI.text, fontStyle: '700',
      }).setOrigin(0, 0.5));
      if (note) m.root.add(this.add.text(m.left, ry + toggleH / 2 + 24, note, {
        fontFamily: SANS, fontSize: '24px', color: UI.textFaint, wordWrap: { width: m.w - 260 },
      }).setOrigin(0, 0.5));
      const sw = this.add.graphics();
      const sx = m.right - 112, sy = ry + toggleH / 2 - 28;
      sw.fillStyle(on ? UI.signal : UI.hairline, on ? 0.9 : 1);
      sw.fillRoundedRect(sx, sy, 112, 56, 28);
      sw.fillStyle(on ? 0x05060d : 0x8d98bd, 1);
      sw.fillCircle(on ? sx + 84 : sx + 28, sy + 28, 20);
      m.root.add(sw);
      const hit = this.add.rectangle(m.cx, ry + toggleH / 2, m.w - 40, toggleH, 0xffffff, 0)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => { this.sfx.ui(); onTap(); });
      m.root.add(hit);
      m.root.add(this.add.rectangle(m.cx, ry + toggleH, m.w - 96, 1, UI.hairline, 1));
      ry += toggleH;
    };
    toggleRow(t('settings_sound'), !this.sfx.muted(), () => { this.sfx.toggleMute(); reopen(); });
    toggleRow(t('settings_haptics'), Progress.hapticsEnabled(),
      () => { Progress.setHaptics(!Progress.hapticsEnabled()); reopen(); }, t('settings_haptics_note'));

    const actionRow = (label: string, color: string, onTap: () => void) => {
      m.root.add(this.add.text(m.left, ry + actionH / 2, label, {
        fontFamily: SANS, fontSize: '30px', color, fontStyle: '700', wordWrap: { width: m.w - 120 },
      }).setOrigin(0, 0.5));
      const hit = this.add.rectangle(m.cx, ry + actionH / 2, m.w - 40, actionH, 0xffffff, 0)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerup', onTap);
      m.root.add(hit);
      m.root.add(this.add.rectangle(m.cx, ry + actionH, m.w - 96, 1, UI.hairline, 1));
      ry += actionH;
    };

    // Cloud sync only appears once sign-in is configured (auth-config.ts).
    if (cloud) {
      const who = user ? (user.name || user.email || 'signed in') : '';
      actionRow(user ? tf('settings_sync_now', { who }) : t('settings_sign_in'), UI.coolHex, () => {
        this.sfx.ui();
        Cloud.syncNow().then((merged) => {
          if (merged) { this.cameras.main.flash(180, 20, 60, 80); reopen(); }
        });
      });
      if (user) actionRow(t('settings_sign_out'), UI.textDim, () => { this.sfx.ui(); Cloud.signOut(); reopen(); });
    }

    actionRow(clean(t('settings_replay_tutorials')), UI.accentHex, () => {
      this.sfx.ui(); Progress.resetTutorials(); m.root.destroy();
      this.cameras.main.flash(160, 20, 40, 80);
    });
    actionRow(clean(resetArmed ? t('settings_reset_confirm') : t('settings_reset_progress')),
      resetArmed ? '#ff5577' : '#ff7a99', () => {
        if (!resetArmed) { this.sfx.ui(); reopen(true); return; }
        Progress.reset();
        this.cameras.main.flash(220, 80, 20, 40);
        this.time.delayedCall(420, () => window.location.reload());
      });

    // Plain words about ads, always visible, then the version (long press opens the test only dev panel).
    m.root.add(this.add.text(m.left, ry + 24, AD_COPY.about_title, {
      fontFamily: SANS, fontSize: '30px', color: UI.warm, fontStyle: '800',
    }));
    m.root.add(this.add.text(m.left, ry + 70, AD_COPY.about_body, {
      fontFamily: SANS, fontSize: '30px', color: UI.textDim, wordWrap: { width: m.w - 96 }, lineSpacing: 6,
    }));
    const verY = m.cy + m.h / 2 - 200;
    m.root.add(this.add.text(m.cx, verY, 'v' + APP_VERSION, {
      fontFamily: SANS, fontSize: '30px', color: UI.textFaint,
    }).setOrigin(0.5));
    if (DEV_MENU_ENABLED) {
      let hold: Phaser.Time.TimerEvent | undefined;
      const verHit = this.add.rectangle(m.cx, verY, 360, 100, 0xffffff, 0).setInteractive();
      verHit.on('pointerdown', () => { hold = this.time.delayedCall(900, () => { m.root.destroy(); openDevMenu(this); }); });
      const cancel = () => { hold?.remove(false); hold = undefined; };
      verHit.on('pointerup', cancel); verHit.on('pointerout', cancel);
      m.root.add(verHit);
    }

    const dismiss = () => { this.sfx.ui(); m.root.destroy(); };
    m.root.add(uiButton(this, m.cx, m.cy + m.h / 2 - 86, m.w - 96, 108, t('settings_done'), UI.accent, dismiss));
    m.dim.on('pointerup', dismiss);
  }

  /** Achievements overlay: every badge, unlocked or locked. */
  private showAchievements() {
    const { height } = this.scale;
    const list = achievementList();
    const avail = height - 120 - 140 - 170 - 40;
    const rowH = Math.min(104, Math.floor(avail / list.length));
    const m = openModal(this, {
      title: `${t('ach_title')}  ${unlockedCount()} / ${ACHIEVEMENTS.length}`,
      accent: 0xffd070, accentHex: UI.warm,
      h: 140 + list.length * rowH + 170,
    });
    list.forEach(({ def, unlocked }, i) => {
      const ry = m.top + i * rowH + rowH / 2 - 8;
      const dot = this.add.graphics();
      dot.fillStyle(unlocked ? 0xffd070 : UI.hairline, 1);
      dot.fillCircle(m.left + 14, ry, 14);
      m.root.add(dot);
      m.root.add(this.add.text(m.left + 52, ry - 15, def.title, {
        fontFamily: SANS, fontSize: '28px',
        color: unlocked ? UI.text : UI.textFaint, fontStyle: '800',
      }).setOrigin(0, 0.5));
      m.root.add(this.add.text(m.left + 52, ry + 20, def.desc, {
        fontFamily: SANS, fontSize: '24px', color: unlocked ? UI.textDim : UI.textFaint,
        wordWrap: { width: m.w - 160 },
      }).setOrigin(0, 0.5));
    });
    const dismiss = () => { this.sfx.ui(); m.root.destroy(); };
    m.root.add(uiButton(this, m.cx, m.cy + m.h / 2 - 86, m.w - 96, 108, t('stats_close'), 0xffd070, dismiss));
    m.dim.on('pointerup', dismiss);
  }

  /** Tiny language selector in the top-right of the menu. */
  private buildLanguagePicker(width: number, _height: number) {
    const cur = getLang();
    const pickerX = width - 72 - 70;
    const pickerY = 112;
    
    const chip = this.add.rectangle(pickerX, pickerY, 140, 64, 0x0a1024, 0.9)
      .setStrokeStyle(2, 0x88a8ff, 0.85);
    // The visible chip is small; the touch target is 132 design px (about 48 css px) tall.
    const chipHit = this.add.rectangle(pickerX, pickerY, 200, 132, 0xffffff, 0).setInteractive({ useHandCursor: true });
    const chipText = this.add.text(pickerX, pickerY, LANGS.find(l => l.code === cur)?.nativeLabel ?? 'EN', {
      fontFamily: FONT_STACK,
      fontSize: '30px',
      color: '#eaf2ff',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    let open = false;
    let dropdown: Phaser.GameObjects.Container | null = null;

    const closeDropdown = () => {
      open = false;
      dropdown?.destroy();
      dropdown = null;
    };

    chipHit.on('pointerdown', () => {
      if (open) { closeDropdown(); return; }
      open = true;
      dropdown = this.add.container(0, 0).setDepth(1000);
      const itemH = 72;
      const dropW = 300;
      const dropX = pickerX;
      const dropY = pickerY + 44;
      const dropBg = this.add.rectangle(dropX, dropY + (LANGS.length * itemH) / 2, dropW, LANGS.length * itemH + 12, 0x0a1024, 0.98)
        .setStrokeStyle(2, 0x88a8ff, 0.95);
      dropdown.add(dropBg);

      LANGS.forEach((lang, i) => {
        const y = dropY + i * itemH + itemH / 2;
        const isCur = lang.code === getLang();
        const itemHit = this.add.rectangle(dropX, y, dropW - 8, itemH - 2, 0xffffff, 0)
          .setInteractive({ useHandCursor: true });
        const label = this.add.text(dropX - 130, y, lang.nativeLabel, {
          fontFamily: FONT_STACK,
          fontSize: '26px',
          color: isCur ? '#88e8ff' : '#eaf2ff',
          fontStyle: 'bold',
        }).setOrigin(0, 0.5);
        const sub = this.add.text(dropX + 130, y, lang.asciiLabel, {
          fontFamily: FONT_STACK,
          fontSize: '20px',
          color: isCur ? '#5a647e' : '#4a6cc0',
        }).setOrigin(1, 0.5);
        dropdown!.add([itemHit, label, sub]);

        itemHit.on('pointerover', () => label.setColor('#ffffff'));
        itemHit.on('pointerout',  () => label.setColor(isCur ? '#88e8ff' : '#eaf2ff'));
        itemHit.on('pointerdown', () => {
          setLang(lang.code as LangCode);
          this.scene.restart();
        });
      });
    });
  }

  /** Shared card shell: rounded panel, hover feedback, tap guard against scroll drags. */
  private cardShell(
    x: number, y: number, w: number, h: number,
    opts: { fill: number; stroke: number; strokeAlpha: number; tap?: () => void; flash?: [number, number, number] },
  ) {
    const panel = new Panel(this, x, y, w, h, {
      fill: opts.fill, fillAlpha: 0.94, stroke: opts.stroke, strokeAlpha: opts.strokeAlpha,
    });
    if (!opts.tap) return;
    const hit = this.add.rectangle(x + w / 2, y + h / 2, w, h, 0xffffff, 0)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => {
      this.sfx.ui();
      panel.restyle({ stroke: opts.stroke, strokeAlpha: 1, strokeWidth: 3 });
    });
    hit.on('pointerout', () => panel.restyle({ stroke: opts.stroke, strokeAlpha: opts.strokeAlpha, strokeWidth: 2 }));
    hit.on('pointerup', () => {
      if (this._dragDist > 12) return;
      this.sfx.tap();
      const f = opts.flash ?? [20, 40, 80];
      this.cameras.main.flash(110, f[0], f[1], f[2]);
      this.time.delayedCall(110, opts.tap!);
    });
  }

  private heroCard(
    x: number, y: number, w: number, h: number,
    c: { fill: number; accent: number; accentHex: string; eyebrow: string; title: string;
         line: string; foot: string; footRight: string; badge?: string; glyph: string; tap: () => void;
         flash: [number, number, number] },
  ) {
    this.cardShell(x, y, w, h, { fill: c.fill, stroke: c.accent, strokeAlpha: 0.85, tap: c.tap, flash: c.flash });
    new Panel(this, x + 28, y + 30, 6, h - 60, { fill: c.accent, fillAlpha: 1, strokeWidth: 0, radius: 3 });

    this.add.text(x + 60, y + 26, c.eyebrow, {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: c.accentHex, fontStyle: '700',
    });
    this.add.text(x + 60, y + 62, c.title, {
      fontFamily: SANS, fontSize: '56px', color: UI.text, fontStyle: '800',
    });
    const lineTxt = this.add.text(x + 60, y + 128, c.line, {
      fontFamily: SANS, fontSize: `${TYPE.label + 2}px`, color: UI.textDim,
      wordWrap: { width: w - 240 },
    });
    // The description must end above the footer row (footer text top is about h - 52),
    // however long a translation runs: step the size down until it does.
    for (let px = TYPE.label + 2; lineTxt.y + lineTxt.height > y + h - 62 && px > 18; px -= 2) {
      lineTxt.setFontSize(px);
    }
    this.add.text(x + 60, y + h - 34, c.foot, {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: UI.textFaint, fontStyle: '700',
    }).setOrigin(0, 0.5);
    this.add.text(x + w - 40, y + h - 34, c.footRight, {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: c.accentHex, fontStyle: '700',
    }).setOrigin(1, 0.5);
    if (c.badge) {
      this.add.text(x + w - 40, y + 30, c.badge, {
        fontFamily: SANS, fontSize: `${TYPE.label}px`, color: UI.textFaint, fontStyle: '700',
      }).setOrigin(1, 0);
    }
    this.add.text(x + w - 40, y + h / 2 - 8, c.glyph, {
      fontFamily: SANS, fontSize: '72px', color: c.accentHex,
    }).setOrigin(1, 0.5);
  }

  /** First launch: the daily and endless cards lead to level 1, where the one-tap lesson lives. */
  private startFirstLevel() {
    const ch = CHAPTERS[0];
    const first = ch.slots.find((s): s is string => !!s);
    if (first) this.scene.start('Game', { levelId: first, chapterId: ch.id });
  }

  /** Today's daily challenge, one shared puzzle with a streak. */
  private makeDailyCard(x: number, y: number, w: number, h: number) {
    const key = dailyKeyFor();
    const solved = Progress.isDailySolved(key);
    const streak = Progress.liveDailyStreak(key, dailyKeyYesterday());
    const fresh = Progress.isNewPlayer();
    this.heroCard(x, y, w, h, {
      fill: 0x08202a,
      accent: solved ? UI.signal : UI.cool,
      accentHex: solved ? UI.signalHex : UI.coolHex,
      eyebrow: '◆',
      title: t('daily_title'),
      line: t('daily_tagline'),
      foot: streak > 0 ? `${t('daily_streak')} ×${streak}${solved ? '' : '  ·  ' + t('daily_keep')}` : '',
      footRight: fresh ? t('menu_play_first') : (solved ? t('daily_done') : t('daily_new')),
      badge: dailyDateLabel(key),
      glyph: solved ? '✓' : '›',
      flash: [20, 60, 60],
      // A brand new player is sent to the one-tap tutorial first, not a daily they cannot read yet.
      tap: () => fresh ? this.startFirstLevel() : this.scene.start('Game', { levelId: dailyIdFor(key) }),
    });
  }

  /** The infinite procedural entry. Always unlocked, always resumes. */
  private makeEndlessCard(x: number, y: number, w: number, h: number) {
    const nextIdx = Progress.nextEndlessIndex();   // 0-based
    const best = Progress.endlessBest();           // -1 if none
    const bestStreak = Progress.endlessBestStreak();
    const hiScore = Progress.bestScore();
    this.heroCard(x, y, w, h, {
      fill: 0x1c0d2a,
      accent: UI.hot,
      accentHex: UI.hotHex,
      eyebrow: '∞',
      title: 'ENDLESS',
      line: t('menu_endless_line'),
      foot: best >= 0
        ? (bestStreak > 1 ? tf('menu_best_streak', { n: best + 1, s: bestStreak }) : tf('menu_best', { n: best + 1 }))
        : '',
      footRight: Progress.isNewPlayer() ? t('menu_play_first') : (best >= 0 ? tf('menu_resume', { n: nextIdx + 1 }) : t('menu_start')),
      badge: hiScore > 0 ? `HI ${fmtScore(hiScore)}` : undefined,
      glyph: '›',
      flash: [60, 20, 50],
      tap: () => {
        if (Progress.isNewPlayer()) { this.startFirstLevel(); return; }
        // Entering from the menu starts a fresh run: top up lives, reset score.
        Progress.refillLives();
        Progress.resetRunScore();
        this.scene.start('Game', { levelId: 'p' + Progress.nextEndlessIndex() });
      },
    });
  }

  private makeChapterCard(
    x: number, y: number, w: number, h: number,
    ch: typeof CHAPTERS[number],
    unlocked: boolean,
    chapterSolved: boolean,
  ) {
    const realIds = ch.slots.filter((s): s is string => !!s);
    const realCount = realIds.length;
    const solvedCount = realIds.filter(id => Progress.isSolved(id)).length;

    const accent = chapterSolved ? UI.signal : (unlocked ? ch.accent : UI.hairline);
    const accentHex = chapterSolved ? UI.signalHex : (unlocked ? ch.accentHex : UI.textFaint);

    this.cardShell(x, y, w, h, {
      fill: unlocked ? UI.surface : 0x080d1c,
      stroke: accent,
      strokeAlpha: unlocked ? 0.8 : 0.6,
      tap: unlocked ? () => this.scene.start('Chapter', { chapterId: ch.id }) : undefined,
    });

    this.add.text(x + 40, y + 26, `CH.${String(ch.num).padStart(2, '0')}`, {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: accentHex, fontStyle: '700',
    });

    if (!unlocked) {
      this.add.text(x + 40, y + 70, t('locked'), {
        fontFamily: SANS, fontSize: '44px', color: UI.textFaint, fontStyle: '800',
      });
      padlock(this, x + w - 64, y + h / 2, 0x4a5a82);
      return;
    }

    this.add.text(x + 40, y + 62, chapterName(ch), {
      fontFamily: SANS, fontSize: '46px', color: UI.text, fontStyle: '800',
    });
    this.add.text(x + 40, y + 116, chapterTagline(ch), {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: UI.textDim,
      wordWrap: { width: w - 260 },
    });

    const dotY = y + h - 26;
    dotRow(this, x + 48, dotY, ch.slots.map((slot) => slot !== null && Progress.isSolved(slot)), accent);

    this.add.text(x + w - 40, y + h - 30, chapterSolved ? t('cleared') : `${solvedCount}/${realCount}`, {
      fontFamily: SANS, fontSize: `${TYPE.label}px`, color: accentHex, fontStyle: '700',
    }).setOrigin(1, 0.5);
    this.add.text(x + w - 40, y + 70, '›', {
      fontFamily: SANS, fontSize: '72px', color: accentHex,
    }).setOrigin(1, 0.5);
  }
}
